import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import type { Patient } from '@/types/patient-api';

/**
 * PUT /api/Patients/{id} — patient edit-save.
 *
 * Mirrors Flutter `updatePatientData()` in
 * `patient_data_source_impl.dart:84`:
 *
 *   await client.put(
 *     '${(await patientAPI(PatientService.Patients))}/$id',
 *     data: patientBody,
 *   );
 *
 * **What's stripped from the body** (verified against live PUT curl
 * 2026-05-14):
 *   - `emergencyContactResponse` — saved separately via
 *     `POST /Patients/EmergencyContact` (`useSaveEmergencyContacts`)
 *   - `healthInsuranceResponse` — saved via `POST /Patients/HealthInsurance`
 *     (`useSaveHealthInsurance`)
 *   - Address rows on `updatePatientContactRequest.patientAddress` with
 *     empty `contactId` get that field removed (backend assigns IDs on
 *     create)
 *
 * **What's RETAINED** (counter-intuitive — Flutter source suggested
 * stripping these too, but the live wire keeps them):
 *   - `corporateAgreementResponse` — ships in the PUT body. The curl
 *     includes the full array. Stripping would silently delete corporate
 *     agreements on edit-save.
 *   - `updatePatientContactRequest` — contact details fold into the PUT
 *     body, not a separate call (unlike the create flow).
 *
 * **Wire-casing on `patientMRN`** (uppercase R): the GET response
 * returns `patientMrn` (lowercase) but the PUT body expects
 * `patientMRN`. We rename on send.
 *
 * **DO NOT use `/api/Patients/CreateUpdatePerson`** for this. That
 * endpoint is reserved for the inline create-appointment patient-create
 * scenario (`useCreatePatient`). The patient-edit screen uses
 * `PUT /Patients/{id}` regardless of which tab is dirty.
 *
 * Cache: invalidates the affected patient + the list query so a
 * back-navigate refetches fresh.
 */
interface UpdatePatientCommand {
  patientId: string;
  patient: Patient;
}

export function useUpdatePatient() {
  const qc = useQueryClient();
  return useMutation<Patient, Error, UpdatePatientCommand>({
    mutationFn: ({ patientId, patient }) => {
      const body = toWireBody(patient, patientId);
      return put<Patient>(`/api/Patients/${patientId}`, body);
    },
    onSuccess: (_data, vars) => {
      // Invalidate the patient — do NOT prime the cache with the PUT response.
      //
      // The PUT returns a partial / differently-shaped body vs. the GET: the
      // request (`toWireBody`) strips `emergencyContactResponse` +
      // `healthInsuranceResponse` and renames `patientMrn`→`patientMRN`, and the
      // backend echoes that shape back. The previous `setQueryData(['patient',
      // id], data)` therefore OVERWROTE the complete cached patient with an
      // incomplete one — so the next navigation to that patient bound partial
      // data (and, since edit-save merges the fetched patient beneath the form
      // via `toPatientBody(values, existing)`, the next PUT dropped more).
      //
      // `refetchType: 'none'` marks the query stale WITHOUT an immediate
      // refetch — this preserves the anti-call-storm intent from PR #246
      // (Andrew, 2026-05-20: invalidate-with-refetch re-fired the GET while the
      // appointment-context modal was mid-unmount). The authoritative full GET
      // fires on the NEXT patient open: the personal-details save navigates back
      // to the list (standalone) so the edit page fully remounts against a stale
      // query → one clean refetch.
      qc.invalidateQueries({
        queryKey: ['patient', vars.patientId],
        refetchType: 'none',
      });
      qc.invalidateQueries({ queryKey: ['patients-list'] });
    },
  });
}

/**
 * Build the PUT body. Strips the two sub-resource arrays (Flutter parity)
 * and the empty-contactId address rows, then sets `patientId` on the
 * body so the backend's model binder gets the same value as the URL.
 */
/**
 * Exported for unit-test verification. The test drives this directly
 * to snapshot the PUT body shape against the live Flutter reference
 * curl (Samer PR #108 review). NOT for runtime consumption outside
 * this hook.
 */
export function toWireBody(patient: Patient, patientId: string): Record<string, unknown> {
  // patientMRN casing rename — ONE-WAY ONLY.
  //
  // Wire asymmetry (verified 2026-05-14):
  //   GET /api/Patients/{id}   →  `patientMrn` (lowercase r)
  //   PUT /api/Patients/{id}   ←  `patientMRN` (uppercase R)
  //
  // The React `Patient` type carries `patientMrn` (matches GET). We rename
  // ONLY here, ONLY on send. Round-trip safety:
  //   1. GET response → React state holds `patientMrn`
  //   2. User edits other fields → form is dirty
  //   3. PUT body is built here → destructured `patientMrn` becomes
  //      `patientMRN` in the spread
  //   4. Next GET refresh → server returns `patientMrn` again → loop closes
  //
  // No double-rename risk because:
  //   - The receive path NEVER sees `patientMRN` (the wire-out name)
  //   - The send path NEVER sees a `patientMRN` field on the React object
  //     (the type doesn't declare it, the destructure pulls `patientMrn`
  //     out before the spread, and the spread can't accidentally include
  //     both because they're different keys on an in-memory object)
  //
  // Also strips the two sub-resources that ship through their own
  // endpoints (`emergencyContactResponse`, `healthInsuranceResponse`).
  // Retains `corporateAgreementResponse` and `updatePatientContactRequest`
  // — both ship in the PUT body per the live curl.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    emergencyContactResponse,
    healthInsuranceResponse,
    patientMrn,
    ...rest
  } = patient as Patient & {
    emergencyContactResponse?: unknown;
    healthInsuranceResponse?: unknown;
  };

  const body: Record<string, unknown> = {
    ...rest,
    patientId,
    patientMRN: patientMrn ?? null,
  };

  // Clean address rows: drop `contactId` when blank (Flutter parity for
  // server-assigned ids).
  const contactReq = patient.updatePatientContactRequest;
  if (contactReq?.patientAddress) {
    const cleaned = contactReq.patientAddress.map((row) => {
      if (!row?.contactId || row.contactId === '') {
        const { contactId: _drop, ...rowRest } = row;
        return rowRest;
      }
      return row;
    });
    body.updatePatientContactRequest = { ...contactReq, patientAddress: cleaned };
  }

  return body;
}
